import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography, Divider, styled, Switch, FormControlLabel, Stack, } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { v4 } from 'uuid';
import { navStore, useStore } from '@mcn-platform/models';
import PostItem from './PostItem';
// import PostEdit from './PostEdit';
import CustomButton from '../common/CustomButton';
import SearchEntryField from '../textSearch/SearchEntryField';
import countMatches from './countMatches';
const StyledDivider = styled(Divider)(() => ({
    borderBottomWidth: 2.5,
    marginTop: 0.4,
    marginBottom: 1,
}));
const getList = (store, topic, adminMode) => {
    // Posts load via pgvector search (eventually consistent: a new/edited post may lag
    // ~1-2s on refetch — see optimistic handling in handleSave). The status filter below
    // is a client hint only: the portal search resolver enforces approved-only server-side
    // regardless of what we send, so this adminMode branch cannot surface pending/rejected
    // posts on the portal. Moderation of pending posts is done from the admin app.
    const list = store.getCachedList('discussionPost', {
        topicID: { eq: topic.key },
        ...(adminMode ? {} : { status: { eq: 'approved' } }),
    }, { field: 'createdAt', direction: 'desc' });
    return list;
};
const DiscussionGroup = ({ topic, startingAdminMode = false }) => {
    const [postsByParent, setPostsByParent] = useState({});
    const [adminMode, setAdminMode] = useState(startingAdminMode);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeEditItem, setActiveEditItem] = useState(null);
    const store = useStore();
    const adminModeAllowed = !!store.currentUser;
    const updatePostsByParent = (list) => {
        // take the lists and organize them by thread
        const byParent = list.items.reduce((acc, post) => {
            if (post.repliesTo) {
                acc[post.repliesTo.key] = acc[post.repliesTo.key] || [];
                acc[post.repliesTo.key].push(post);
            }
            else {
                acc.ROOT = acc.ROOT || [];
                acc.ROOT.push(post);
            }
            return acc;
        }, {});
        console.log('byParent', byParent);
        setPostsByParent(byParent);
    };
    const fetchData = useCallback(async () => {
        // console.log('topicID', id);
        const list = getList(store, topic, adminMode);
        list.reset();
        list.setRequestedCount(100);
        await store.fetchCachedList(list);
        console.log('list.items', list.items);
        // setListOfPost(list.items);
        updatePostsByParent(list);
    }, [adminMode, store, topic]);
    useEffect(() => {
        fetchData();
    }, [store, topic, fetchData]);
    const editPost = (postToEdit) => {
        setActiveEditItem(postToEdit);
    };
    const startPost = (repliesTo) => {
        const newPost = store.stub('discussionPost', v4(), store.currentCustomer.name || '', {
            organization: store.currentOrganization,
            topicID: topic.key,
            content: '',
            userID: store.currentUser?.id,
            customerID: store.currentCustomer?.id,
            repliesTo,
            status: topic.approvalRequired ? 'new' : 'approved',
        });
        const listKey = repliesTo?.key || 'ROOT';
        const newPostsByParent = {
            ...postsByParent,
            [listKey]: [newPost, ...(postsByParent[listKey] || [])],
        };
        setPostsByParent(newPostsByParent);
        setActiveEditItem(newPost);
    };
    const savePost = async (postToUpdate) => {
        navStore.showBlockInput();
        if (postToUpdate.syncState === 'stubbed') {
            // we're posting a new item
            await store.addStub('discussionPost', postToUpdate, false);
        }
        else {
            // saving an existing item
            await store.update('discussionPost', postToUpdate);
        }
        // await new Promise((resolve) => { setTimeout(resolve, 500); });
        // await fetchData();
        navStore.clearBlockInput();
        setActiveEditItem(undefined);
    };
    const deletePost = async (postToDelete) => {
        navStore.showConfirmDialog('DeletePost', 'Are you sure you want to delete the post?', async () => {
            navStore.showBlockInput();
            await store.remove('discussionPost', postToDelete);
            // await new Promise((resolve) => { setTimeout(resolve, 500); });
            // await fetchData();
            navStore.clearBlockInput();
            setActiveEditItem(undefined);
            updatePostsByParent(getList(store, topic, adminMode));
        }, 'Delete');
    };
    const cancelEdit = () => {
        setActiveEditItem(undefined);
    };
    if (store.isAnonymous) {
        return _jsx("div", { children: "You must be logged in to participate in discussions." });
    }
    if (!topic) {
        return null;
    }
    // console.log(topic);
    if (topic.syncState === 'error' || topic.syncState === 'not_found') {
        return _jsx("div", { children: "Error loading topic" });
    }
    return (_jsxs(Box, { children: [_jsxs(Stack, { direction: "row", spacing: 1, sx: { my: 1 }, justifyContent: "space-between", alignItems: "center", children: [_jsxs(Box, { children: [_jsx(Typography, { variant: "subtitle1", fontWeight: "fontWeightBold", children: topic.name }), _jsx(Typography, { variant: "body1", sx: { pt: 1, pb: 2, whiteSpace: 'pre-wrap' }, children: topic.description })] }), _jsxs(Stack, { direction: "column", spacing: 1, children: [adminModeAllowed && (_jsx(FormControlLabel, { control: (_jsx(Switch, { checked: adminMode, onClick: () => setAdminMode(!adminMode) })), label: "Admin Mode" })), _jsx(SearchEntryField, { searchTerm: searchTerm, setSearchTerm: setSearchTerm })] })] }), _jsx(StyledDivider, {}), _jsx(CustomButton, { startIcon: _jsx(AddIcon, {}), label: "Post", disabled: activeEditItem, customStyle: { my: 2, px: 2 }, onClick: () => startPost(undefined) }), postsByParent?.ROOT?.map((postItem) => (countMatches(postItem, searchTerm, postsByParent) > 0 ? (_jsx(PostItem, { postItem: postItem, editItem: activeEditItem, postsByParent: postsByParent, editPost: editPost, startPost: startPost, savePost: savePost, cancelEdit: cancelEdit, deletePost: deletePost, adminMode: adminMode, searchTerm: searchTerm }, postItem.key)) : null))] }));
};
export default observer(DiscussionGroup);
//# sourceMappingURL=DiscussionGroup.js.map